const encoder = new TextEncoder();
const decoder = new TextDecoder();

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function asArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.slice().buffer as ArrayBuffer;
}

async function importAesKey(keyInput: string, usage: KeyUsage[]): Promise<CryptoKey> {
  if (!keyInput || typeof keyInput !== "string" || !keyInput.trim()) {
    throw new Error("CREDENTIAL_MASTER_KEY 未配置，请在 Cloudflare 环境变量或 Secret 中设置 CREDENTIAL_MASTER_KEY");
  }
  let raw: Uint8Array | null = null;
  try {
    const decoded = fromBase64(keyInput.trim());
    if (decoded.byteLength === 32) {
      raw = decoded;
    }
  } catch {
    // Not standard base64, fall back to raw or SHA-256 derivation
  }
  if (!raw) {
    const encoded = encoder.encode(keyInput.trim());
    if (encoded.byteLength === 32) {
      raw = encoded;
    } else {
      const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
      raw = new Uint8Array(hashBuffer);
    }
  }
  return crypto.subtle.importKey("raw", asArrayBuffer(raw), "AES-GCM", false, usage);
}

export interface EncryptedSecret {
  ciphertext: string;
  iv: string;
  version: number;
}

export async function encryptSecret(
  plaintext: string,
  base64Key: string,
  version = 1,
): Promise<EncryptedSecret> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await importAesKey(base64Key, ["encrypt"]);
  const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoder.encode(plaintext));
  return { ciphertext: toBase64(new Uint8Array(encrypted)), iv: toBase64(iv), version };
}

export async function decryptSecret(secret: EncryptedSecret, base64Key: string): Promise<string> {
  const key = await importAesKey(base64Key, ["decrypt"]);
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: asArrayBuffer(fromBase64(secret.iv)) },
    key,
    asArrayBuffer(fromBase64(secret.ciphertext)),
  );
  return decoder.decode(decrypted);
}

export async function hashGatewayKey(value: string, pepper: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    "raw",
    encoder.encode(pepper),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const signature = await crypto.subtle.sign("HMAC", key, encoder.encode(value));
  return toBase64(new Uint8Array(signature));
}

export function createGatewayKey(): { id: string; value: string; prefix: string } {
  const id = crypto.randomUUID();
  const secret = toBase64(crypto.getRandomValues(new Uint8Array(32)))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");
  const prefix = id.slice(0, 8);
  return { id, prefix, value: `ftr_live_${prefix}.${secret}` };
}
